/**
 * UI8 Hero Sphere Component
 * Interactive infinite-scroll sphere with product card thumbnails.
 * Usage: import { initHero } from './hero-sphere.js'; initHero(containerEl, 'cards/');
 * Returns a params object that can be mutated at runtime (e.g. via tweakpane).
 */
const CARDS = [
  'card_3','card_8','card_9','card_11','card_12','card_13','card_14','card_16',
  'card_18','card_19','card_20','card_21','card_22','card_23','card_24','card_26',
  'card_27','card_28','card_29','card_31','card_33','card_34','card_36','card_38',
  'card_40','card_42','card_45','card_49','card_52','card_53','card_58','card_59',
  'card_66','card_68','card_70','card_73','card_82','card_83','card_84','card_85',
  'card_86','card_87','card_88','card_95','card_96','card_102','card_103','card_107',
  'card_109','card_112'
];

function prng(a) {
  return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

function initHero(el, basePath = '/img/cards/', glassEl = null, cardCount = 0) {
  // Pages that don't need the full deck can ask for fewer textures (each card
  // is a separate image request). Sampled evenly across the list rather than
  // sliced off the front, so a smaller set keeps the variety of the full one.
  // Cards still tile the sphere either way — they just repeat sooner.
  const deck =
    cardCount > 0 && cardCount < CARDS.length
      ? Array.from({ length: cardCount }, (_, i) =>
          CARDS[Math.round((i * (CARDS.length - 1)) / (cardCount - 1))]
        )
      : CARDS;

  const P = {
    bgColor: '#121212',
    sphereRadius: 30,
    cardW: 3.6,
    cardH: 2.5,
    gridGap: 2.2,
    depthRange: 4,
    autoRotateSpeed: 0.0001,
    sphereSpacing: 120,
    sphereCount: 5,
    scrollSpeed: 0.15,
    dragSensitivity: 0.003,
    dragSmoothing: 0.3,
    friction: 0.95,
    scrollFriction: 0.92,
    fadeInDuration: 4,
    camZ: 45.7,
    initRotX: 0.078,
    fov: 50,
    facingMultiplier: 0.5,
    facingOffset: 0.4,
    backMinOutside: 0,
    backMinInside: 0.55,
    distFadeMult: 1.3,
    breathMinSpeed: 0.3,
    breathSpeedRange: 0.3,
    breathMinAmp: 0.05,
    breathAmpRange: 0.08,
    cardCornerRadius: 24,
    maxRotX: 0.6,
    autoZoomSpeed: 0,
    tiltAmp: 0.005,
    tiltSpeed: 0.15,
    scrollZoom: false,
    sphereOffsetY: 0,
    trailStrength: 0.8,
    overlayOpacity: 0.4,
    grainIntensity: 0.13,
    grainScale: 1,
    vignetteRadius: 0,
    vignetteSoftness: 0.46,
    vignetteIntensity: 1.0,
    vignetteRoundness: 1.0,
    glassIor: 1.11,
    glassBlur: 12,
    glassEdgeBright: 0.0,
    glassLightGrad: 0.0,
    glassTintR: 0.1,
    glassTintG: 0.1,
    glassTintB: 0.1,
    glassTintStrength: 0.0,
    glassBrightness: -0.02,
    glassDistortion: 0.1,
    glassChromaticAb: 1.2,
    glassLiquid: 1.2,
    glassOffsetW: 0.001,
    glassOffsetH: 0.002,
    mouseParallax: 0,
    glassVisible: 0,
    ringThickness: 0.14,
    ringOffset: 0.90,
    ringIor: 1.25,
    ringBlur: 13.5,
    ringChromaAb: 3.5,
    ringBrightness: 0.03,
    ringOpacity: 1.0,
  };

  THREE.ColorManagement.enabled = false;
  const rand = prng(42);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setSize(el.clientWidth, el.clientHeight);
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  el.appendChild(renderer.domElement);

  // Render targets for post-processing + trails
  const pr = renderer.getPixelRatio();
  let rt = new THREE.WebGLRenderTarget(el.clientWidth * pr, el.clientHeight * pr);

  // Vignette post-process
  const vignetteVert = `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;
  const vignetteFrag = `
    uniform sampler2D u_scene;
    uniform vec3 u_bgColor;
    uniform float u_radius;
    uniform float u_softness;
    uniform float u_intensity;
    uniform float u_roundness;
    uniform vec2 u_resolution;
    uniform float u_grainIntensity;
    uniform float u_grainScale;
    uniform float u_time;
    uniform vec2 u_glassCenter;
    uniform vec2 u_glassSize;
    uniform float u_glassIor;
    uniform float u_glassBlur;
    uniform float u_glassEdgeBright;
    uniform float u_glassLightGrad;
    uniform vec3 u_glassTint;
    uniform float u_glassTintStrength;
    uniform float u_glassBrightness;
    uniform float u_glassDistortion;
    uniform float u_glassChromaticAb;
    uniform float u_glassLiquid;
    uniform vec2 u_glassOffsetSize;
    uniform float u_glassOpacity;
    uniform float u_ringThickness;
    uniform float u_ringOffset;
    uniform float u_ringIor;
    uniform float u_ringBlur;
    uniform float u_ringChromaAb;
    uniform float u_ringBrightness;
    uniform float u_ringOpacity;
    varying vec2 vUv;
    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    float dither(vec2 co) { return (fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0; }

    void main() {
      vec2 m2 = vUv - u_glassCenter;
      // Pill/stadium SDF in aspect-corrected space so caps are truly circular
      float aspect = u_resolution.x / u_resolution.y;
      float mx = m2.x * aspect;
      float sizeX = (u_glassSize.x + u_glassOffsetSize.x) * aspect;
      float sizeY = u_glassSize.y + u_glassOffsetSize.y;
      float pillExcess = sizeX - sizeY; // straight section half-length
      float ax = max(abs(mx) - pillExcess, 0.0);
      float ay = abs(m2.y);
      float pillDist = length(vec2(ax, ay)) / sizeY;
      float roundedBox = pillDist * pillDist;

      // Masks: interior, edge ring, outer glow
      float rb1 = clamp((1.0 - roundedBox) * 8.0, 0.0, 1.0);
      float rb2 = clamp((0.95 - roundedBox * 0.95) * 16.0, 0.0, 1.0) -
                  clamp((0.9 - roundedBox * 0.95) * 16.0, 0.0, 1.0);
      float rb3 = clamp((1.5 - roundedBox * 1.1) * 2.0, 0.0, 1.0) -
                  clamp((1.0 - roundedBox * 1.1) * 2.0, 0.0, 1.0);

      float transition = smoothstep(0.0, 1.0, rb1 + rb2);
      vec3 col;

      if (transition > 0.0) {
        // Lens distortion: magnify toward pill center
        float lensStr = (u_glassIor - 1.0) * 3.0;
        vec2 fromCenter = vUv - u_glassCenter;
        vec2 lens = fromCenter * (1.0 - roundedBox * lensStr) + u_glassCenter;

        // Extra barrel/pincushion distortion
        float r2 = dot(fromCenter / u_glassSize, fromCenter / u_glassSize);
        vec2 barrel = fromCenter * (1.0 + u_glassDistortion * r2) - fromCenter;
        lens += barrel * 0.05;

        // Liquid wobble: layered sine waves for organic flowing distortion
        float liq = u_glassLiquid * 0.003 * (1.0 - roundedBox);
        lens.x += liq * (sin(vUv.y * 40.0 + u_time * 1.3) + 0.5 * sin(vUv.y * 70.0 - u_time * 2.1 + vUv.x * 20.0));
        lens.y += liq * (cos(vUv.x * 35.0 + u_time * 1.7) + 0.5 * cos(vUv.x * 60.0 + u_time * 1.9 + vUv.y * 25.0));


        // Golden angle spiral Gaussian blur — no banding, smooth disc
        vec3 blurredR = vec3(0.0);
        vec3 blurredG = vec3(0.0);
        vec3 blurredB = vec3(0.0);
        float totalW = 0.0;
        float caOffset = u_glassChromaticAb * 0.002;
        vec2 dir = normalize(fromCenter + 0.001);
        float spread = u_glassBlur / u_resolution.y;
        const float SAMPLES = 40.0;
        const float GOLDEN_ANGLE = 2.399963;
        for (float i = 0.0; i < SAMPLES; i++) {
          float r = sqrt(i / SAMPLES) * spread;
          float theta = i * GOLDEN_ANGLE;
          vec2 offset = vec2(cos(theta), sin(theta)) * r;
          float w = exp(-i / SAMPLES * 2.0);
          blurredR += texture2D(u_scene, lens + offset + dir * caOffset).rgb * w;
          blurredG += texture2D(u_scene, lens + offset).rgb * w;
          blurredB += texture2D(u_scene, lens + offset - dir * caOffset).rgb * w;
          totalW += w;
        }
        vec4 blurred = vec4(blurredR.r / totalW, blurredG.g / totalW, blurredB.b / totalW, 1.0);

        // Brightness boost
        blurred.rgb += u_glassBrightness;

        // Color tint
        blurred.rgb = mix(blurred.rgb, u_glassTint, u_glassTintStrength);

        // Top-lit gradient (simulates light from above reflecting on glass)
        float gradient = clamp((clamp(m2.y, 0.0, 0.2) + 0.1) / 2.0, 0.0, 1.0) +
                         clamp((clamp(-m2.y, -1000.0, 0.2) * rb3 + 0.1) / 2.0, 0.0, 1.0);
        vec4 lighting = clamp(blurred + vec4(rb1) * gradient * u_glassLightGrad + vec4(rb2) * u_glassEdgeBright, 0.0, 1.0);

        col = mix(texture2D(u_scene, vUv).rgb, lighting.rgb, transition * u_glassOpacity);
      } else {
        col = texture2D(u_scene, vUv).rgb;
      }

      // Ring glass: thin band of intense dispersion at the pill edge
      float ringDist = abs(pillDist - u_ringOffset);
      float ringMask = smoothstep(u_ringThickness, 0.0, ringDist) * u_ringOpacity * u_glassOpacity;
      if (ringMask > 0.001) {
        float ringLensStr = (u_ringIor - 1.0) * 4.0;
        vec2 ringFromCenter = vUv - u_glassCenter;
        vec2 ringLens = ringFromCenter * (1.0 - pillDist * ringLensStr * 0.3) + u_glassCenter;

        // Chromatic aberration — stronger on the ring for dispersion look
        vec2 ringDir = normalize(ringFromCenter + 0.001);
        float ringCa = u_ringChromaAb * 0.003;

        // Blur with fewer samples (ring is thin, doesn't need 128)
        vec3 rR = vec3(0.0), rG = vec3(0.0), rB = vec3(0.0);
        float rW = 0.0;
        float rSpread = u_ringBlur / u_resolution.y;
        const float RING_SAMPLES = 24.0;
        for (float i = 0.0; i < RING_SAMPLES; i++) {
          float r = sqrt(i / RING_SAMPLES) * rSpread;
          float theta = i * 2.399963;
          vec2 off = vec2(cos(theta), sin(theta)) * r;
          float w = exp(-i / RING_SAMPLES * 2.0);
          rR += texture2D(u_scene, ringLens + off + ringDir * ringCa).rgb * w;
          rG += texture2D(u_scene, ringLens + off).rgb * w;
          rB += texture2D(u_scene, ringLens + off - ringDir * ringCa).rgb * w;
          rW += w;
        }
        vec3 ringCol = vec3(rR.r / rW, rG.g / rW, rB.b / rW);
        ringCol += u_ringBrightness;
        col = mix(col, ringCol, ringMask);
      }

      // Grain
      float grain = (hash(floor(gl_FragCoord.xy / u_grainScale) + u_time) - 0.5) * u_grainIntensity;
      col *= (1.0 + grain);

      // Vignette — top and bottom only
      float dist = abs(vUv.y - 0.5);
      float vig = smoothstep(u_radius, u_radius + u_softness, dist) * u_intensity;
      col = mix(col, u_bgColor, vig) + dither(gl_FragCoord.xy);
      gl_FragColor = vec4(col, 1.0);
    }`;
  const vignetteMat = new THREE.ShaderMaterial({
    uniforms: {
      u_scene: { value: rt.texture },
      u_bgColor: { value: new THREE.Color(P.bgColor) },
      u_radius: { value: P.vignetteRadius },
      u_softness: { value: P.vignetteSoftness },
      u_intensity: { value: P.vignetteIntensity },
      u_roundness: { value: P.vignetteRoundness },
      u_resolution: { value: new THREE.Vector2(el.clientWidth, el.clientHeight) },
      u_grainIntensity: { value: P.grainIntensity },
      u_grainScale: { value: P.grainScale },
      u_time: { value: 0 },
      u_glassCenter: { value: new THREE.Vector2(0.5, 0.5) },
      u_glassSize: { value: new THREE.Vector2(0.15, 0.03) },
      u_glassOpacity: { value: 0 },
      u_ringThickness: { value: P.ringThickness },
      u_ringOffset: { value: P.ringOffset },
      u_ringIor: { value: P.ringIor },
      u_ringBlur: { value: P.ringBlur },
      u_ringChromaAb: { value: P.ringChromaAb },
      u_ringBrightness: { value: P.ringBrightness },
      u_ringOpacity: { value: P.ringOpacity },
      u_glassIor: { value: P.glassIor },
      u_glassBlur: { value: P.glassBlur },
      u_glassEdgeBright: { value: P.glassEdgeBright },
      u_glassLightGrad: { value: P.glassLightGrad },
      u_glassTint: { value: new THREE.Color(P.glassTintR, P.glassTintG, P.glassTintB) },
      u_glassTintStrength: { value: P.glassTintStrength },
      u_glassBrightness: { value: P.glassBrightness },
      u_glassDistortion: { value: P.glassDistortion },
      u_glassChromaticAb: { value: P.glassChromaticAb },
      u_glassLiquid: { value: P.glassLiquid },
      u_glassOffsetSize: { value: new THREE.Vector2(P.glassOffsetW, P.glassOffsetH) },
    },
    vertexShader: vignetteVert, fragmentShader: vignetteFrag, depthTest: false, depthWrite: false,
  });
  const vignetteQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), vignetteMat);
  const vignetteScene = new THREE.Scene();
  vignetteScene.add(vignetteQuad);
  const vignetteCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(P.bgColor);
  const camera = new THREE.PerspectiveCamera(P.fov, el.clientWidth / el.clientHeight, 0.1, 500);
  const updateCam = () => {
    camera.fov = P.fov;
    camera.aspect = el.clientWidth / el.clientHeight;
    camera.updateProjectionMatrix();
    camera.position.set(0, 0, P.camZ);
    camera.lookAt(0, 0, P.camZ - 100);
  };
  updateCam();

  // Textures
  const aniso = renderer.capabilities.getMaxAnisotropy();
  const makeTex = (img) => {
    const c = document.createElement('canvas'); c.width = 604; c.height = 420;
    const ctx = c.getContext('2d'); ctx.clearRect(0, 0, 604, 420);
    ctx.beginPath(); ctx.roundRect(0, 0, 604, 420, P.cardCornerRadius); ctx.closePath();
    if (img) { ctx.clip(); ctx.drawImage(img, 0, 0, 604, 420); } else { ctx.fillStyle = '#2a2a2a'; ctx.fill(); }
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.LinearSRGBColorSpace; if (img) t.anisotropy = aniso; return t;
  };
  const placeholder = makeTex(null);
  const textures = deck.map(() => placeholder);
  deck.forEach((n, i) => { const img = new Image(); img.onload = () => { textures[i] = makeTex(img); }; img.src = basePath + n + '.avif'; });

  // Overlay shader
  const overlayVert = `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
  const overlayFrag = `uniform sampler2D map; uniform vec3 u_overlayColor; uniform float u_overlayOpacity; varying vec2 vUv;
void main() { vec4 tex = texture2D(map, vUv); gl_FragColor = vec4(tex.rgb * (1.0 - u_overlayOpacity) + u_overlayColor * u_overlayOpacity, tex.a); }`;
  const overlayColor = new THREE.Color(P.bgColor);

  // Spheres
  const R = P.sphereRadius;
  const geo = new THREE.PlaneGeometry(P.cardW, P.cardH);
  const angH = (P.cardH * P.gridGap) / R, rows = Math.floor(Math.PI / angH), startPhi = (Math.PI - (rows - 1) * angH) / 2;
  let gIdx = 0;

  const makeSphere = () => {
    const g = new THREE.Group(), cards = [];
    for (let r = 0; r < rows; r++) {
      const phi = startPhi + r * angH, sp = Math.sin(phi), cp = Math.cos(phi);
      const cols = Math.max(1, Math.floor(2 * Math.PI * sp * R / (P.cardW * P.gridGap)));
      const aw = (2 * Math.PI) / cols, off = (r % 2) ? aw * 0.5 : 0;
      for (let c = 0; c < cols; c++) {
        const th = c * aw + off;
        const sv = new THREE.Vector3(R * sp * Math.cos(th), R * cp, R * sp * Math.sin(th));
        const pos = sv.clone().addScaledVector(sv.clone().normalize(), (rand() - 0.5) * P.depthRange);
        const ti = gIdx++ % deck.length;
        const m = new THREE.Mesh(geo, new THREE.ShaderMaterial({
          uniforms: { map: { value: textures[ti] }, u_overlayColor: { value: overlayColor }, u_overlayOpacity: { value: 0.0 } },
          vertexShader: overlayVert, fragmentShader: overlayFrag, transparent: true, side: THREE.DoubleSide,
        }));
        m.position.copy(pos);
        m.userData = { ti, bp: pos.clone(), bpn: pos.clone().normalize(), ph: rand() * Math.PI * 2, bs: P.breathMinSpeed + rand() * P.breathSpeedRange, ba: P.breathMinAmp + rand() * P.breathAmpRange };
        cards.push(m); g.add(m);
      }
    }
    g.userData = { rotY: Math.random() * Math.PI * 2, rotX: P.initRotX, baseZ: 0 };
    return { group: g, cards };
  };

  const spheres = [];
  for (let i = 0; i < P.sphereCount; i++) { const s = makeSphere(); s.group.userData.baseZ = -i * P.sphereSpacing; s.group.position.z = s.group.userData.baseZ; scene.add(s.group); spheres.push(s); }

  // Interaction
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  let drag = false, dx = 0, dy = 0, dvx = 0, dvy = 0, tvx = 0, tvy = 0, sv = 0;
  el.addEventListener('mousedown', e => { drag = true; dx = e.clientX; dy = e.clientY; });
  window.addEventListener('mousemove', e => { if (!drag) return; tvx = -(e.clientX - dx) * P.dragSensitivity; tvy = -(e.clientY - dy) * P.dragSensitivity; dx = e.clientX; dy = e.clientY; });
  window.addEventListener('mouseup', () => { drag = false; });
  el.addEventListener('touchstart', e => { drag = true; dx = e.touches[0].clientX; dy = e.touches[0].clientY; }, { passive: true });
  window.addEventListener('touchmove', e => { if (!drag) return; tvx = -(e.touches[0].clientX - dx) * P.dragSensitivity; if (!isMobile) tvy = -(e.touches[0].clientY - dy) * P.dragSensitivity; dx = e.touches[0].clientX; dy = e.touches[0].clientY; }, { passive: true });
  window.addEventListener('touchend', () => { drag = false; }, { passive: true });
  el.addEventListener('wheel', e => { if (!P.scrollZoom) return; e.preventDefault(); sv += e.deltaY * P.scrollSpeed; }, { passive: false });

  // Mouse parallax (non-drag, subtle rotation following cursor)
  let mouseNormX = 0, mouseNormY = 0, smoothMX = 0, smoothMY = 0;
  window.addEventListener('mousemove', e => {
    mouseNormX = (e.clientX / window.innerWidth - 0.5) * 2;
    mouseNormY = (e.clientY / window.innerHeight - 0.5) * 2;
  });

  // Glass hover/focus animation targets
  let glassTargetTint = P.glassTintStrength;
  let glassTargetEdge = P.glassEdgeBright;
  let glassCurrentTint = P.glassTintStrength;
  let glassCurrentEdge = P.glassEdgeBright;
  const GLASS_LERP = 0.08;
  if (glassEl) {
    const inputEl = glassEl.querySelector('input') || glassEl;
    inputEl.addEventListener('mouseenter', () => { glassTargetTint = 0.45; });
    inputEl.addEventListener('mouseleave', () => { if (document.activeElement !== inputEl) glassTargetTint = P.glassTintStrength; });
    inputEl.addEventListener('focus', () => { glassTargetEdge = 1.0; glassTargetTint = 0.45; });
    inputEl.addEventListener('blur', () => { glassTargetEdge = P.glassEdgeBright; glassTargetTint = P.glassTintStrength; });
  }

  // Animate
  const clock = new THREE.Clock();
  const total = P.sphereCount * P.sphereSpacing, half = total / 2;
  // Per-frame scratch objects — the card loop must not allocate
  const _bq = new THREE.Quaternion();
  const _wp = new THREE.Vector3();
  const _tc = new THREE.Vector3();
  const _cn = new THREE.Vector3();

  let paused = false;

  let lastW = 0, lastH = 0;
  (function loop() {
    requestAnimationFrame(loop);
    if (paused) return;
    const cw = el.clientWidth, ch = el.clientHeight;
    if (cw !== lastW || ch !== lastH) { lastW = cw; lastH = ch; const p = renderer.getPixelRatio(); renderer.setSize(cw, ch); rt.setSize(cw * p, ch * p); vignetteMat.uniforms.u_resolution.value.set(cw, ch); updateCam(); }
    const t = clock.getElapsedTime(), fade = Math.min(t / P.fadeInDuration, 1);

    scene.background.set(P.bgColor);
    overlayColor.set(P.bgColor);

    if (drag) { dvx += (tvx - dvx) * P.dragSmoothing; dvy += (tvy - dvy) * P.dragSmoothing; tvx = 0; tvy = 0; } else { dvx *= P.friction; dvy *= P.friction; }
    sv *= P.scrollFriction; P.camZ -= sv * 0.05; P.camZ -= P.autoZoomSpeed; updateCam();
    // Smooth mouse parallax
    const ml = 1 - Math.exp(-0.016 * 5);
    smoothMX += (mouseNormX - smoothMX) * ml;
    smoothMY += (mouseNormY - smoothMY) * ml;

    spheres.forEach(({ group: g, cards }) => {
      const u = g.userData;
      let rz = u.baseZ - P.camZ;
      while (rz > half) { u.baseZ -= total; rz -= total; }
      while (rz < -half) { u.baseZ += total; rz += total; }
      g.position.z = u.baseZ;
      g.position.y = P.sphereOffsetY;
      u.rotY += dvx + P.autoRotateSpeed;
      u.rotX = drag ? Math.max(-P.maxRotX, Math.min(P.maxRotX, u.rotX + dvy)) : u.rotX + P.tiltAmp * 0.01;
      g.rotation.y = u.rotY + smoothMX * P.mouseParallax;
      g.rotation.x = u.rotX + smoothMY * P.mouseParallax * 0.5;

      const zd = Math.abs(g.position.z - P.camZ);
      const ins = 1 - Math.min(zd / R, 1), minB = P.backMinOutside + (P.backMinInside - P.backMinOutside) * ins;
      const fd = Math.max(0, zd - R * 1.8), dl = Math.max(0, 1 - fd / (P.sphereSpacing * P.distFadeMult)), db = Math.pow(dl, 5);

      // Fully faded spheres are pure background — skip their draw calls
      // and per-card math entirely (they re-enter as they scroll close)
      const sphereVisible = db * fade > 0.002;
      g.visible = sphereVisible;
      if (!sphereVisible) return;

      // Billboarding is identical for every card in the group — compute once
      g.updateMatrixWorld();
      _bq.copy(g.quaternion).invert().multiply(camera.quaternion);

      cards.forEach(card => {
        const d = card.userData;
        if (card.material.uniforms.map.value !== textures[d.ti]) { card.material.uniforms.map.value = textures[d.ti]; }
        card.position.copy(d.bp).addScaledVector(d.bpn, Math.sin(t * d.bs + d.ph) * d.ba);
        card.quaternion.copy(_bq);
        _wp.copy(card.position); g.localToWorld(_wp);
        _tc.copy(camera.position).sub(_wp).normalize();
        _cn.copy(d.bpn).applyQuaternion(g.quaternion);
        const fb = Math.max(minB, Math.min(1, _tc.dot(_cn) * P.facingMultiplier + P.facingOffset));
        const br = fb * db * fade;
        card.material.uniforms.u_overlayOpacity.value = P.overlayOpacity + (1 - br) * (1 - P.overlayOpacity);
      });
    });
    // Glass pill screen-space coords for vignette shader
    if (glassEl) {
      const rect = glassEl.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const cx = (rect.left + rect.width / 2 - elRect.left) / elRect.width;
      const cy = 1.0 - (rect.top + rect.height / 2 - elRect.top) / elRect.height; // flip Y for GL
      const hw = (rect.width / elRect.width) / 2;
      const hh = (rect.height / elRect.height) / 2;
      vignetteMat.uniforms.u_glassCenter.value.set(cx, cy);
      vignetteMat.uniforms.u_glassSize.value.set(hw, hh);
    }

    // Lerp glass hover/focus animations
    glassCurrentTint += (glassTargetTint - glassCurrentTint) * GLASS_LERP;
    glassCurrentEdge += (glassTargetEdge - glassCurrentEdge) * GLASS_LERP;

    // Render scene to texture, then apply vignette
    vignetteMat.uniforms.u_bgColor.value.set(P.bgColor);
    vignetteMat.uniforms.u_radius.value = P.vignetteRadius;
    vignetteMat.uniforms.u_softness.value = P.vignetteSoftness;
    vignetteMat.uniforms.u_intensity.value = P.vignetteIntensity;
    vignetteMat.uniforms.u_roundness.value = P.vignetteRoundness;
    vignetteMat.uniforms.u_grainIntensity.value = P.grainIntensity;
    vignetteMat.uniforms.u_grainScale.value = P.grainScale;
    vignetteMat.uniforms.u_time.value = t;
    vignetteMat.uniforms.u_glassIor.value = P.glassIor;
    vignetteMat.uniforms.u_glassBlur.value = P.glassBlur;
    vignetteMat.uniforms.u_glassLightGrad.value = P.glassLightGrad;
    vignetteMat.uniforms.u_glassTint.value.set(P.glassTintR, P.glassTintG, P.glassTintB);
    vignetteMat.uniforms.u_glassTintStrength.value = glassCurrentTint;
    vignetteMat.uniforms.u_glassEdgeBright.value = glassCurrentEdge;
    vignetteMat.uniforms.u_glassBrightness.value = P.glassBrightness;
    vignetteMat.uniforms.u_glassDistortion.value = P.glassDistortion;
    vignetteMat.uniforms.u_glassChromaticAb.value = P.glassChromaticAb;
    vignetteMat.uniforms.u_glassLiquid.value = P.glassLiquid;
    vignetteMat.uniforms.u_glassOpacity.value = P.glassVisible;
    vignetteMat.uniforms.u_ringThickness.value = P.ringThickness;
    vignetteMat.uniforms.u_ringOffset.value = P.ringOffset;
    vignetteMat.uniforms.u_ringIor.value = P.ringIor;
    vignetteMat.uniforms.u_ringBlur.value = P.ringBlur;
    vignetteMat.uniforms.u_ringChromaAb.value = P.ringChromaAb;
    vignetteMat.uniforms.u_ringBrightness.value = P.ringBrightness;
    vignetteMat.uniforms.u_ringOpacity.value = P.ringOpacity;
    vignetteMat.uniforms.u_glassOffsetSize.value.set(P.glassOffsetW, P.glassOffsetH);
    // Render scene to rt, skip trail blending for performance
    renderer.setRenderTarget(rt);
    renderer.render(scene, camera);

    vignetteMat.uniforms.u_scene.value = rt.texture;
    renderer.setRenderTarget(null);
    renderer.render(vignetteScene, vignetteCam);
  })();

  window.addEventListener('resize', () => { const w = el.clientWidth, h = el.clientHeight, p = renderer.getPixelRatio(); renderer.setSize(w, h); rt.setSize(w * p, h * p); vignetteMat.uniforms.u_resolution.value.set(w, h); updateCam(); });

  // Page Visibility: auto-pause when tab is hidden, auto-resume when visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { paused = true; } else { paused = false; clock.start(); }
  });

  // Pause/resume API — attach to params object for backward-compatible return
  P.pause = () => { paused = true; };
  P.resume = () => { paused = false; clock.start(); };

  return P;
}

// three.js is loaded on demand (app/views/partials/_shaders.jade). Every
// caller already polls `window.HeroSphere && window.HeroSphere.initHero`
// with requestAnimationFrame, so the first read of HeroSphere is the
// request for three.js, and the API appears once `THREE` is on window.
const HeroSphereAPI = { initHero };
Object.defineProperty(window, 'HeroSphere', {
  configurable: true,
  get() {
    if (window.THREE) return HeroSphereAPI;
    if (window.UI8 && window.UI8.loadShaders) window.UI8.loadShaders();
    return undefined;
  },
});
